/**
 * Centralised debug logger for client-side (Angular) applications.
 *
 * Behaviour:
 *  - `info` and `warn` are only written to the console when NOT running in production.
 *  - `error` is ALWAYS written to the console, even in production, because errors are
 *    vital for catching the pesky little bugs that only surface in the wild.
 *
 * Each application should call `configureDebugLogger(environment.production)` during
 * bootstrap (see the individual `main.ts` files) so the logger knows whether it is
 * running in a production build. This is how the value of `environment.production`
 * reaches this shared helper (a file in `common` cannot import an application specific
 * `environment`).
 *
 * NOTE: This helper is intended for CLIENT-SIDE use only. Do not use it on the server.
 *
 * API:
 *  - `message` is always a `string`.
 *  - `data` is an optional generic value of type `T`.
 */

let isProduction = false;

/**
 * Configure the debug logger. Should be called once per application during bootstrap
 * with the value of `environment.production`.
 */
export function configureDebugLogger(production: boolean): void {
    isProduction = production;
}

export const debugLogger = {
    /**
     * Log an informational message. Suppressed in production builds.
     */
    info<T>(message: string, data?: T): void {
        if (isProduction) {
            return;
        }
        if (data === undefined) {
            console.info(message);
            return;
        }
        console.info(message, data);
    },

    /**
     * Log a warning message. Suppressed in production builds.
     */
    warn<T>(message: string, data?: T): void {
        if (isProduction) {
            return;
        }
        if (data === undefined) {
            console.warn(message);
            return;
        }
        console.warn(message, data);
    },

    /**
     * Log an error message. Always logged, even in production, as errors are vital
     * for catching bugs.
     */
    error<T>(message: string, data?: T): void {
        if (data === undefined) {
            console.error(message);
            return;
        }
        console.error(message, data);
    }
};
